import { memo } from 'react'
import { DateTime } from 'luxon'

/**
 * DateCell Component
 * Individual date button in the calendar grid with multiple visual states
 *
 * @param {Object} props - Component props
 * @param {DateTime} props.date - Luxon DateTime object for this date
 * @param {Function} props.onDateClick - Callback when date is clicked (receives YYYY-MM-DD string)
 * @param {string} props.selectedDate - Currently selected date (YYYY-MM-DD)
 * @param {Set} props.availableDates - Set of available dates (YYYY-MM-DD strings)
 * @param {DateTime} props.currentMonth - Luxon DateTime for the current month being displayed
 */
const DateCell = ({ date, onDateClick, selectedDate, availableDates, currentMonth }) => {
  const dateStr = date.toISODate() // YYYY-MM-DD format
  const dayNumber = date.day
  const today = DateTime.now().startOf('day')

  // Determine date states
  const isToday = date.hasSame(today, 'day')
  const isAvailable = availableDates.has(dateStr)
  const isSelected = dateStr === selectedDate
  const isCurrentMonth = date.hasSame(currentMonth, 'month')

  // Base styles for all date cells
  const baseStyles = "w-full aspect-square flex items-center justify-center rounded-lg text-sm font-medium transition-all duration-300"

  // State-specific styles
  const stateStyles = (() => {
    // Selected state (highest priority)
    if (isSelected) {
      return "bg-secondary text-black font-bold shadow-md scale-105"
    }

    // Available state (clickable)
    if (isAvailable) {
      return "bg-white text-primary border-2 border-secondary/30 hover:bg-secondary/10 hover:border-secondary hover:scale-105 cursor-pointer"
    }

    // Unavailable state (grayed out, same month only)
    if (!isAvailable && isCurrentMonth) {
      return "bg-gray-100 text-gray-400 cursor-not-allowed"
    }

    // Overflow dates (previous/next month)
    if (!isCurrentMonth) {
      return "bg-transparent text-gray-300 cursor-not-allowed"
    }

    // Default fallback
    return "bg-white text-gray-500"
  })()

  // Today indicator (ring around the date)
  const todayRing = isToday ? "ring-2 ring-primary ring-offset-2" : ""

  // Handle click - only trigger for available dates
  const handleClick = () => {
    if (isAvailable) {
      onDateClick(dateStr)
    }
  }

  return (
    <button
      onClick={handleClick}
      disabled={!isAvailable}
      className={`${baseStyles} ${stateStyles} ${todayRing}`}
      aria-label={`${date.toFormat('MMMM d, yyyy')}${isAvailable ? ' - Available' : ' - Unavailable'}${isToday ? ' - Today' : ''}`}
      aria-pressed={isSelected}
      tabIndex={isAvailable ? 0 : -1}
    >
      {dayNumber}
    </button>
  )
}

// Memoize to prevent unnecessary re-renders
export default memo(DateCell)
